import { CellInput, InputKind, InputSheet, InputSheetSection, NamesInput, NumbersInput, SheetInput, SweepCondition, SweepInput, TableInput, TableInputColumn, FILE_COLUMN_TYPES, FileColumnSpec, isFileColumn, normalizeTableRows, sheetInputs } from '../inputs';
import { TableColumnSpec, TableColumnType } from '../types';

/**
 * Editing an input sheet, as the studio's Inputs tab does: adding, moving,
 * copying and removing inputs and sections, and keeping a sweep's conditions
 * set by the report runner tied to the names or numbers inputs that hold them. Every
 * function returns a new sheet and leaves the one it was given alone. Pure.
 */

/** An identifier from a label: lowercase letters, digits and underscores, starting with a letter. */
export function slugId(label: string, fallback = 'input'): string {
  const slug = label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 50);
  return slug || fallback;
}

/** `base`, or `base_2`, `base_3`… — the first not taken. */
export function uniqueId(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) if (!used.has(`${base}_${n}`)) return `${base}_${n}`;
}

/** Every identifier an input on the sheet uses: its own, and a sweep's conditions' and cells'. */
function takenIds(sheet: InputSheet): Set<string> {
  return new Set(sheetInputs(sheet).map((i) => i.id));
}

/** The inputs' identifiers, but for one: what another input's new id must not be. */
export function sheetIds(sheet: InputSheet, except?: string): string[] {
  return sheetInputs(sheet).map((i) => i.id).filter((id) => id !== except);
}

const LABELS: Record<InputKind, string> = {
  number: 'Number', text: 'Text', choice: 'Choice', yesno: 'Yes or no?', date: 'Date', names: 'Names', numbers: 'Numbers',
  image: 'Photo', file: 'File', waveform: 'Capture', table: 'Table', sweep: 'Measurement',
};

/** A new input of a kind, complete enough to draw and to compile, with an id no other input on the sheet has. */
export function newInput(kind: InputKind, sheet: InputSheet): SheetInput {
  const label = LABELS[kind];
  const id = uniqueId(slugId(label), takenIds(sheet));
  switch (kind) {
    case 'number': return { id, kind, label, unit: '' };
    case 'choice': return { id, kind, label, choices: ['Option 1', 'Option 2'] };
    case 'names': return { id, kind, label, defaults: [] };
    case 'numbers': return { id, kind, label, defaults: [] };
    case 'image': return { id, kind, label, multiple: false };
    case 'table': return { id, kind, label, columns: [{ id: 'item', title: 'Item', type: 'string' }, { id: 'value', title: 'Value', type: 'string' }], rows: [] };
    case 'sweep': return {
      id, kind, label, required: true,
      conditions: [{ id: 'condition', label: 'Condition', kind: 'names', values: ['A', 'B'] }],
      record: [{ id: 'value', kind: 'number', label: 'Value', unit: '' }],
      layout: { tabs: [], rows: ['condition'], columns: [] },
    };
    default: return { id, kind, label } as SheetInput;
  }
}

export function newSection(sheet: InputSheet, title = 'New section'): InputSheetSection {
  return { id: uniqueId(slugId(title, 'section'), sheet.sections.map((s) => s.id)), title, inputs: [] };
}

/** Where an input is: its section's index and its own. */
export function locate(sheet: InputSheet, inputId: string): { section: number; index: number } | null {
  for (const [s, section] of sheet.sections.entries()) {
    const index = section.inputs.findIndex((i) => i.id === inputId);
    if (index >= 0) return { section: s, index };
  }
  return null;
}

const withSection = (sheet: InputSheet, s: number, inputs: SheetInput[]): InputSheet => ({
  ...sheet,
  sections: sheet.sections.map((section, i) => (i === s ? { ...section, inputs } : section)),
});

/** The sheet with `input` placed in section `s` at `index` (the end when absent). */
export function insertInput(sheet: InputSheet, s: number, input: SheetInput, index?: number): InputSheet {
  const inputs = [...sheet.sections[s].inputs];
  inputs.splice(index ?? inputs.length, 0, input);
  return withSection(sheet, s, inputs);
}

export function updateInput(sheet: InputSheet, inputId: string, change: (input: SheetInput) => SheetInput): InputSheet {
  const at = locate(sheet, inputId);
  if (!at) return sheet;
  const next = change(sheet.sections[at.section].inputs[at.index]);
  let updated = withSection(sheet, at.section, sheet.sections[at.section].inputs.map((i, k) => (k === at.index ? next : i)));
  // a renamed input takes the sweeps that read it from the sheet along
  if (next.id !== inputId) updated = mapSweeps(updated, (sweep) => ({ ...sweep, conditions: sweep.conditions.map((c) => (c.input === inputId ? { ...c, input: next.id } : c)) }));
  return updated;
}

/** Move an input to section `s` at `index`, counted as if it had already been taken out. */
export function moveInput(sheet: InputSheet, inputId: string, s: number, index: number): InputSheet {
  const at = locate(sheet, inputId);
  if (!at) return sheet;
  const input = sheet.sections[at.section].inputs[at.index];
  const without = withSection(sheet, at.section, sheet.sections[at.section].inputs.filter((_, k) => k !== at.index));
  return insertInput(without, s, input, Math.min(index, without.sections[s].inputs.length));
}

/** One step up or down, into the neighbouring section at its edge. */
export function nudgeInput(sheet: InputSheet, inputId: string, direction: -1 | 1): InputSheet {
  const at = locate(sheet, inputId);
  if (!at) return sheet;
  const count = sheet.sections[at.section].inputs.length;
  const index = at.index + direction;
  if (index >= 0 && index < count) return moveInput(sheet, inputId, at.section, index);
  const s = at.section + direction;
  if (s < 0 || s >= sheet.sections.length) return sheet;
  return moveInput(sheet, inputId, s, direction < 0 ? sheet.sections[s].inputs.length : 0);
}

/** A copy just below, with its own id; a copied sweep keeps reading the same runner inputs. */
export function duplicateInput(sheet: InputSheet, inputId: string): { sheet: InputSheet; id: string } {
  const at = locate(sheet, inputId);
  if (!at) return { sheet, id: inputId };
  const original = sheet.sections[at.section].inputs[at.index];
  const id = uniqueId(original.id.replace(/_\d+$/, ''), takenIds(sheet));
  const copy = { ...JSON.parse(JSON.stringify(original)), id, label: `${original.label} (copy)` } as SheetInput;
  return { sheet: insertInput(sheet, at.section, copy, at.index + 1), id };
}

/** Which sweeps read an input set by the report runner. */
export function sweepsReading(sheet: InputSheet, inputId: string): SweepInput[] {
  return sheetInputs(sheet).filter((i): i is SweepInput => i.kind === 'sweep' && i.conditions.some((c) => c.input === inputId));
}

/**
 * The sheet without an input. A sweep that read it keeps the
 * values it held, fixed in the bundle; a removed sweep takes the runner inputs
 * only it read along with it.
 */
export function removeInput(sheet: InputSheet, inputId: string): InputSheet {
  const at = locate(sheet, inputId);
  if (!at) return sheet;
  const removed = sheet.sections[at.section].inputs[at.index];
  let next = withSection(sheet, at.section, sheet.sections[at.section].inputs.filter((_, k) => k !== at.index));
  if (removed.kind === 'names' || removed.kind === 'numbers') {
    next = mapSweeps(next, (sweep) => ({
      ...sweep,
      conditions: sweep.conditions.map((c) => (c.input === inputId ? fixCondition(c, removed.defaults ?? [], removed.kind as 'names' | 'numbers', removed.kind === 'numbers' ? removed.unit : undefined) : c)),
    }));
  }
  if (removed.kind === 'sweep') {
    for (const condition of removed.conditions) {
      if (condition.input && sweepsReading(next, condition.input).length === 0) next = removeInput(next, condition.input);
    }
  }
  return next;
}

function fixCondition(c: SweepCondition, values: Array<string | number>, kind: 'names' | 'numbers', unit?: string): SweepCondition {
  const { input: _input, ...rest } = c;
  return { ...rest, kind, ...(unit && !rest.unit ? { unit } : {}), values: [...values] };
}

function mapSweeps(sheet: InputSheet, change: (sweep: SweepInput) => SweepInput): InputSheet {
  return { ...sheet, sections: sheet.sections.map((s) => ({ ...s, inputs: s.inputs.map((i) => (i.kind === 'sweep' ? change(i) : i)) })) };
}

export function updateSection(sheet: InputSheet, sectionId: string, change: Partial<Pick<InputSheetSection, 'id' | 'title' | 'help'>>): InputSheet {
  return { ...sheet, sections: sheet.sections.map((s) => (s.id === sectionId ? { ...s, ...change } : s)) };
}

/** Add a section after `afterId` (at the end when absent). */
export function insertSection(sheet: InputSheet, section: InputSheetSection, afterId?: string): InputSheet {
  const at = afterId ? sheet.sections.findIndex((s) => s.id === afterId) + 1 : sheet.sections.length;
  const sections = [...sheet.sections];
  sections.splice(at || sections.length, 0, section);
  return { ...sheet, sections };
}

/** Put a section at a position among the sections, 0 being first. */
export function insertSectionAt(sheet: InputSheet, section: InputSheetSection, index: number): InputSheet {
  const sections = [...sheet.sections];
  sections.splice(Math.max(0, Math.min(index, sections.length)), 0, section);
  return { ...sheet, sections };
}

/** Move a section to a gap between sections, the gap counted with the section still in place. */
export function moveSectionTo(sheet: InputSheet, sectionId: string, gap: number): InputSheet {
  const at = sheet.sections.findIndex((s) => s.id === sectionId);
  if (at < 0) return sheet;
  const sections = sheet.sections.filter((s) => s.id !== sectionId);
  sections.splice(Math.max(0, Math.min(at < gap ? gap - 1 : gap, sections.length)), 0, sheet.sections[at]);
  return { ...sheet, sections };
}

/** Remove a section and everything in it, as removing each input would. */
export function removeSection(sheet: InputSheet, sectionId: string): InputSheet {
  const section = sheet.sections.find((s) => s.id === sectionId);
  if (!section) return sheet;
  let next = sheet;
  for (const input of section.inputs) next = removeInput(next, input.id);
  return { ...next, sections: next.sections.filter((s) => s.id !== sectionId) };
}

export function moveSection(sheet: InputSheet, sectionId: string, direction: -1 | 1): InputSheet {
  const at = sheet.sections.findIndex((s) => s.id === sectionId);
  const to = at + direction;
  if (at < 0 || to < 0 || to >= sheet.sections.length) return sheet;
  const sections = [...sheet.sections];
  [sections[at], sections[to]] = [sections[to], sections[at]];
  return { ...sheet, sections };
}

// ── sweeps: conditions set by the runner or in the bundle ────────────────────────

const RUN_SETUP_SECTION = { id: 'run_setup', title: 'Run setup', help: 'Set by the report runner: what is tested today.' };

/**
 * A sweep's condition set by the report runner: a names or numbers input on the
 * sheet holds its values (its defaults are where a new report starts), in the
 * section the other runner inputs are in — a Run setup section at the top,
 * made when there is none.
 */
export function setConditionByRunner(sheet: InputSheet, sweepId: string, conditionId: string): InputSheet {
  const at = locate(sheet, sweepId);
  const sweep = at ? (sheet.sections[at.section].inputs[at.index] as SweepInput) : null;
  const condition = sweep?.conditions.find((c) => c.id === conditionId);
  if (!sweep || !condition || condition.input) return sheet;
  const kind = condition.kind ?? (condition.values?.every((v) => typeof v === 'number') ? 'numbers' : 'names');
  const id = uniqueId(slugId(`${condition.label || condition.id}s`), takenIds(sheet));
  const input: SheetInput = kind === 'numbers'
    ? { id, kind, label: condition.label, ...(condition.unit ? { unit: condition.unit } : {}), defaults: (condition.values ?? []).filter((v): v is number => typeof v === 'number'), required: true, carryOver: true }
    : { id, kind, label: condition.label, defaults: (condition.values ?? []).map(String), required: true, carryOver: true };
  let next = sheet;
  let runSetupSection = next.sections.findIndex((s) => s.inputs.some((i) => sweepsReading(next, i.id).length > 0));
  if (runSetupSection < 0) {
    next = { ...next, sections: [{ ...RUN_SETUP_SECTION, id: uniqueId(RUN_SETUP_SECTION.id, next.sections.map((s) => s.id)), inputs: [] }, ...next.sections] };
    runSetupSection = 0;
  }
  next = insertInput(next, runSetupSection, input);
  return updateInput(next, sweepId, (s) => ({
    ...(s as SweepInput),
    conditions: (s as SweepInput).conditions.map((c) => {
      if (c.id !== conditionId) return c;
      const { values: _values, ...rest } = c;
      return { ...rest, kind, input: id };
    }),
  }));
}

/** A condition's values fixed in the bundle again: the runner input's defaults, the input itself gone unless another sweep reads it. */
export function setConditionInBundle(sheet: InputSheet, sweepId: string, conditionId: string): InputSheet {
  const at = locate(sheet, sweepId);
  const sweep = at ? (sheet.sections[at.section].inputs[at.index] as SweepInput) : null;
  const condition = sweep?.conditions.find((c) => c.id === conditionId);
  if (!sweep || !condition?.input) return sheet;
  const inputId = condition.input;
  const source = sheetInputs(sheet).find((i) => i.id === inputId);
  const values = source && (source.kind === 'names' || source.kind === 'numbers') ? source.defaults ?? [] : [];
  const kind = source?.kind === 'numbers' ? 'numbers' : 'names';
  let next = updateInput(sheet, sweepId, (s) => ({
    ...(s as SweepInput),
    conditions: (s as SweepInput).conditions.map((c) => (c.id === conditionId ? fixCondition(c, values, kind, source?.kind === 'numbers' ? source.unit : undefined) : c)),
  }));
  if (sweepsReading(next, inputId).length === 0) next = removeInput(next, inputId);
  return next;
}

/** Whether a condition repeats over names or numbers: its runner input's kind, else its own, else what its values are. */
export function conditionKind(sheet: InputSheet, condition: SweepCondition): 'names' | 'numbers' {
  const source = condition.input ? sheetInputs(sheet).find((i) => i.id === condition.input) : undefined;
  if (source?.kind === 'names' || source?.kind === 'numbers') return source.kind;
  return condition.kind ?? (condition.values?.every((v) => typeof v === 'number') ? 'numbers' : 'names');
}

/** The inputs the report runner can set a condition from: every names or numbers input of its kind, wherever it is on the sheet. */
export function runnerCandidates(sheet: InputSheet, condition: SweepCondition): Array<NamesInput | NumbersInput> {
  const kind = conditionKind(sheet, condition);
  return sheetInputs(sheet).filter((i): i is NamesInput | NumbersInput => i.kind === kind);
}

/**
 * A condition set by the report runner from an input already on the sheet, in
 * whatever section it is. The condition takes the input's label, and its
 * values and unit come from the input from then on; the input it read before,
 * if any, goes unless another sweep reads it. Linking an input of the other
 * kind changes nothing.
 */
export function linkCondition(sheet: InputSheet, sweepId: string, conditionId: string, inputId: string): InputSheet {
  const at = locate(sheet, sweepId);
  const sweep = at ? (sheet.sections[at.section].inputs[at.index] as SweepInput) : null;
  const condition = sweep?.conditions.find((c) => c.id === conditionId);
  if (!sweep || !condition || condition.input === inputId) return sheet;
  const source = runnerCandidates(sheet, condition).find((i) => i.id === inputId);
  if (!source) return sheet;
  const previous = condition.input;
  let next = updateInput(sheet, sweepId, (s) => ({
    ...(s as SweepInput),
    conditions: (s as SweepInput).conditions.map((c) => {
      if (c.id !== conditionId) return c;
      const { values: _values, unit: _unit, ...rest } = c;
      return { ...rest, label: source.label, kind: source.kind, input: source.id };
    }),
  }));
  if (previous && sweepsReading(next, previous).length === 0) next = removeInput(next, previous);
  return next;
}

/** The values a condition starts with: its own, or the defaults of the runner input holding them. */
export function conditionDefaults(sheet: InputSheet, condition: SweepCondition): Array<string | number> {
  if (!condition.input) return condition.values ?? [];
  const source = sheetInputs(sheet).find((i) => i.id === condition.input);
  return source && (source.kind === 'names' || source.kind === 'numbers') ? source.defaults ?? [] : [];
}

/** Set a condition's starting values: on the condition, or on its runner input's defaults. */
export function setConditionDefaults(sheet: InputSheet, sweepId: string, conditionId: string, values: Array<string | number>): InputSheet {
  const at = locate(sheet, sweepId);
  const condition = at ? (sheet.sections[at.section].inputs[at.index] as SweepInput).conditions.find((c) => c.id === conditionId) : undefined;
  if (!condition) return sheet;
  if (condition.input) {
    return updateInput(sheet, condition.input, (i) => (i.kind === 'numbers' ? { ...i, defaults: values.filter((v): v is number => typeof v === 'number') } : i.kind === 'names' ? { ...i, defaults: values.map(String) } : i));
  }
  return updateInput(sheet, sweepId, (s) => ({ ...(s as SweepInput), conditions: (s as SweepInput).conditions.map((c) => (c.id === conditionId ? { ...c, values } : c)) }));
}

/** A new condition, fixed in the bundle, on the rows if the sweep has none there, the columns otherwise. */
export function addCondition(sweep: SweepInput, kind: 'names' | 'numbers'): SweepInput {
  const id = uniqueId(kind === 'numbers' ? 'level' : 'condition', sweep.conditions.map((c) => c.id));
  const condition: SweepCondition = { id, label: kind === 'numbers' ? 'Level' : 'Condition', kind, values: [] };
  const shelf = sweep.layout.rows.length === 0 ? 'rows' : sweep.layout.columns.length === 0 ? 'columns' : 'tabs';
  return { ...sweep, conditions: [...sweep.conditions, condition], layout: { ...sweep.layout, [shelf]: [...sweep.layout[shelf], id] } };
}

/** The sweep without a condition, off its shelf too. (A runner input only it read is the caller's to remove.) */
export function removeCondition(sweep: SweepInput, conditionId: string): SweepInput {
  const drop = (ids: string[]) => ids.filter((i) => i !== conditionId);
  return { ...sweep, conditions: sweep.conditions.filter((c) => c.id !== conditionId), layout: { ...sweep.layout, tabs: drop(sweep.layout.tabs), rows: drop(sweep.layout.rows), columns: drop(sweep.layout.columns) } };
}

export type Shelf = 'tabs' | 'rows' | 'columns';

/** Put a condition on a shelf, at `index` there (the end when absent). */
export function shelveCondition(sweep: SweepInput, conditionId: string, shelf: Shelf, index?: number): SweepInput {
  const drop = (ids: string[]) => ids.filter((i) => i !== conditionId);
  const layout = { ...sweep.layout, tabs: drop(sweep.layout.tabs), rows: drop(sweep.layout.rows), columns: drop(sweep.layout.columns) };
  const target = [...layout[shelf]];
  target.splice(index ?? target.length, 0, conditionId);
  return { ...sweep, layout: { ...layout, [shelf]: target } };
}

/** A new input to record in each cell, with an id the sweep's other cell inputs do not have. */
export function newCellInput(sweep: SweepInput, kind: CellInput['kind']): CellInput {
  const label = LABELS[kind];
  const id = uniqueId(slugId(label), sweep.record.map((r) => r.id));
  return (kind === 'number' ? { id, kind, label, unit: '' } : kind === 'choice' ? { id, kind, label, choices: ['Option 1', 'Option 2'] } : { id, kind, label }) as CellInput;
}

/**
 * A condition relabelled. While it is first named (`follow`) its id follows,
 * shelf and all; after that the id stays put, since the report's code and the
 * readings entered read it.
 */
export function relabelCondition(sweep: SweepInput, conditionId: string, label: string, follow = false): SweepInput {
  const condition = sweep.conditions.find((c) => c.id === conditionId);
  if (!condition) return sweep;
  const others = sweep.conditions.filter((c) => c.id !== conditionId).map((c) => c.id);
  const id = follow ? uniqueId(slugId(label, 'condition'), others) : condition.id;
  return setConditionId({ ...sweep, conditions: sweep.conditions.map((c) => (c.id === conditionId ? { ...c, label } : c)) }, conditionId, id);
}

/** A condition under a new id, on its shelf too. Readings are kept by condition id, so ones entered before no longer line up. */
export function setConditionId(sweep: SweepInput, conditionId: string, id: string): SweepInput {
  if (id === conditionId) return sweep;
  const swap = (ids: string[]) => ids.map((i) => (i === conditionId ? id : i));
  return {
    ...sweep,
    conditions: sweep.conditions.map((c) => (c.id === conditionId ? { ...c, id } : c)),
    layout: { ...sweep.layout, tabs: swap(sweep.layout.tabs), rows: swap(sweep.layout.rows), columns: swap(sweep.layout.columns) },
  };
}

/** One input recorded in each cell, relabelled; its id follows only while it is first named (`follow`). */
export function relabelCellInput(sweep: SweepInput, index: number, label: string, follow = false): SweepInput {
  const input = sweep.record[index];
  if (!input) return sweep;
  const others = sweep.record.filter((_, i) => i !== index).map((r) => r.id);
  const id = follow ? uniqueId(slugId(label, 'value'), others) : input.id;
  return { ...sweep, record: sweep.record.map((r, i) => (i === index ? { ...r, label, id } : r)) };
}

/** A new table column, its id from its title. */
export function newColumn(columns: TableInputColumn[], title = 'Column'): TableInputColumn {
  return { id: uniqueId(slugId(title, 'column'), columns.map((c) => c.id)), title, type: 'string' };
}

/** A column as another type: what it held for its old type goes, but a typed column keeps its unit and choices. */
export function retypeColumn(column: TableInputColumn, type: TableInputColumn['type']): TableInputColumn {
  const base = { id: column.id, title: column.title, ...(column.help ? { help: column.help } : {}), ...(column.width ? { width: column.width } : {}) };
  if ((FILE_COLUMN_TYPES as readonly string[]).includes(type)) return { ...base, type: type as FileColumnSpec['type'] };
  const typed: Partial<TableColumnSpec> = isFileColumn(column) ? {} : column;
  return {
    ...base,
    type: type as TableColumnType,
    ...(typed.unit ? { unit: typed.unit } : {}),
    ...(type === 'choice' ? { choices: typed.choices ?? ['Option 1'] } : typed.choices ? { choices: typed.choices } : {}),
  };
}

/** A table's column under a new id; its starting rows keep their cells under it. */
export function setColumnId(table: TableInput, columnId: string, id: string): TableInput {
  if (id === columnId) return table;
  const rows = table.rows?.map((row) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k === columnId ? id : k, v])));
  return { ...table, columns: table.columns.map((c) => (c.id === columnId ? { ...c, id } : c)), ...(rows ? { rows } : {}) };
}

/** A table with new columns, its starting rows brought up to them: a removed column's cells go, a retyped one's are coerced, a file column's are empty. */
export function setColumns(table: TableInput, columns: TableInput['columns']): TableInput {
  return { ...table, columns, ...(table.rows ? { rows: normalizeTableRows(columns, table.rows, false) } : {}) };
}

/** What the report receives from a sweep, by the ids its code reads: `input_current.current [function × supply_voltage] A`. */
export function sweepSignature(sweep: SweepInput): string {
  const dims = sweep.conditions.map((c) => c.id).join(' × ');
  return sweep.record.map((r) => `${sweep.id}.${r.id} [${dims}]${r.kind === 'number' && r.unit ? ` ${r.unit}` : r.kind === 'number' ? '' : ` ${r.kind}`}`).join('\n');
}

/** Units related to one by an SI prefix, for quick picks: A → mA, A, kA. */
export function unitPicks(unit: string): string[] {
  const match = /^(p|n|µ|u|m|k|M|G)?(A|V|W|Ω|Hz|s|g|F|H|Pa|J|lm|cd)$/.exec(unit.trim());
  if (!match) return [];
  const base = match[2];
  return base === 's' ? ['µs', 'ms', 's'] : base === 'Hz' ? ['Hz', 'kHz', 'MHz'] : [`m${base}`, base, `k${base}`];
}
